import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Box, Button, Typography } from '@mui/material'

import { Member } from 'types/organization'

import { snakeToLabel } from 'utils/snakeToLabel'

import { useMediaSize } from 'hooks/useMediaSize'

import { useRemoveOrganizationMemberMutation } from 'services/organizations'

import { ReactComponent as DeleteIcon } from 'assets/svg/buttons/DeleteIcon.svg'
import { ReactComponent as EditIcon } from 'assets/svg/buttons/EditIcon.svg'

import ConfirmModal from 'components/ConfirmModal'
import { TModalVariant } from 'components/Modal/types'
import OrganizationMemberActionModal from 'components/OrganizationMemberActionModal'

import s from './styles'

interface Props {
  members?: Member[]
}

const OrganizationMembers: FC<Props> = ({ members }) => {
  const { t } = useTranslation()
  const { isPhone } = useMediaSize()
  const { organizationId } = useParams()

  const [modalMode, setModalMode] = useState<TModalVariant>(null)
  const [member, serMember] = useState<Member | undefined>(undefined)

  const [remove, { isLoading: isDeleteLoading }] = useRemoveOrganizationMemberMutation()

  const openEditModal = (member: Member) => {
    setModalMode('edit')
    serMember(member)
  }

  const openDeleteConfirmModal = (member: Member) => {
    setModalMode('delete')
    serMember(member)
  }

  const handleDeleteMember = () => {
    if (organizationId && member)
      remove(member?.memberId)
        .unwrap()
        .then(() => {
          setModalMode(null)
        })
        .catch(() => null)
  }

  return (
    <>
      <Typography variant="h4" sx={s.heading}>
        {t('Pages.Organizations.Members')}
      </Typography>
      <Box sx={s.container}>
        {members?.map((member) => (
          <Box sx={s.item} key={member.memberId}>
            <Box sx={s.name}>
              {member.memberFirstName} {member.memberLastName}
            </Box>
            <Box sx={s.permissions}>
              {t('Pages.Organizations.Permissions')}:{' '}
              {member.permissions.map(snakeToLabel).join(', ')}
            </Box>
            <Box sx={s.buttons}>
              <EditIcon onClick={() => openEditModal(member)} />
              <DeleteIcon onClick={() => openDeleteConfirmModal(member)} />
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        variant="contained"
        sx={{ mt: 3 }}
        fullWidth={isPhone}
        onClick={() => setModalMode('create')}
      >
        {t('Pages.Organizations.AddNewMember')}
      </Button>
      {modalMode === 'create' && (
        <OrganizationMemberActionModal
          organizationId={organizationId}
          variant={'create'}
          onClose={() => setModalMode(null)}
        />
      )}
      {modalMode === 'edit' && (
        <OrganizationMemberActionModal
          member={member}
          variant={'edit'}
          onClose={() => setModalMode(null)}
        />
      )}

      {modalMode === 'delete' && (
        <ConfirmModal
          variant="delete"
          onConfirm={handleDeleteMember}
          loading={isDeleteLoading}
          onClose={() => setModalMode(null)}
          text={t('Pages.Organizations.RemoveConfirmation')}
        />
      )}
    </>
  )
}

export default OrganizationMembers
